import { AlertCircle, CheckCircle, Info, Loader2, XCircle } from "lucide-react";
import { createRoot } from "react-dom/client";
import { cn } from "@/lib/utils";

type MessageType = "success" | "error" | "warning" | "info" | "loading";

interface MessageConfig {
	content: React.ReactNode;
	duration?: number;
	key?: string;
	onClose?: () => void;
}

interface MessageInstance {
	id: string;
	type: MessageType;
	content: React.ReactNode;
	duration: number;
	onClose?: () => void;
}

// 消息容器
let messageContainer: HTMLDivElement | null = null;
let messageRoot: ReturnType<typeof createRoot> | null = null;
let messages: MessageInstance[] = [];
let messageId = 0;

const getContainer = () => {
	if (!messageContainer) {
		messageContainer = document.createElement("div");
		messageContainer.className =
			"fixed top-4 left-1/2 -translate-x-1/2 z-[2000] flex flex-col items-center gap-2 pointer-events-none";
		document.body.appendChild(messageContainer);
		messageRoot = createRoot(messageContainer);
	}
	return messageRoot!;
};

const iconMap = {
	success: CheckCircle,
	error: XCircle,
	warning: AlertCircle,
	info: Info,
	loading: Loader2,
};

const colorMap = {
	success: "text-success-500",
	error: "text-error-500",
	warning: "text-warning-500",
	info: "text-primary-500",
	loading: "text-primary-500",
};

const MessageItem = ({ instance }: { instance: MessageInstance }) => {
	const Icon = iconMap[instance.type];

	return (
		<div
			className={cn(
				"flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 pointer-events-auto",
				"animate-in slide-in-from-top-2 fade-in-0 duration-200",
			)}
		>
			<Icon
				className={cn(
					"h-4 w-4 flex-shrink-0",
					colorMap[instance.type],
					instance.type === "loading" && "animate-spin",
				)}
			/>
			<span className="text-sm text-gray-700 dark:text-gray-200">{instance.content}</span>
		</div>
	);
};

const MessageList = () => {
	return (
		<>
			{messages.map((msg) => (
				<MessageItem key={msg.id} instance={msg} />
			))}
		</>
	);
};

const render = () => {
	const root = getContainer();
	root.render(<MessageList />);
};

const removeMessage = (id: string) => {
	const index = messages.findIndex((m) => m.id === id);
	if (index > -1) {
		const msg = messages[index];
		msg.onClose?.();
		messages = messages.filter((m) => m.id !== id);
		render();
	}
};

const addMessage = (type: MessageType, config: MessageConfig | React.ReactNode) => {
	const normalizedConfig: MessageConfig =
		typeof config === "object" && config !== null && "content" in config
			? config
			: { content: config };

	const id = `msg_${++messageId}`;
	const duration = normalizedConfig.duration ?? (type === "loading" ? 0 : 3);

	const instance: MessageInstance = {
		id,
		type,
		content: normalizedConfig.content,
		duration,
		onClose: normalizedConfig.onClose,
	};

	messages = [...messages, instance];
	render();

	if (duration > 0) {
		setTimeout(() => {
			removeMessage(id);
		}, duration * 1000);
	}

	return () => removeMessage(id);
};

// 公开 API
export const message = {
	success: (config: MessageConfig | React.ReactNode) => addMessage("success", config),
	error: (config: MessageConfig | React.ReactNode) => addMessage("error", config),
	warning: (config: MessageConfig | React.ReactNode) => addMessage("warning", config),
	info: (config: MessageConfig | React.ReactNode) => addMessage("info", config),
	loading: (config: MessageConfig | React.ReactNode) => addMessage("loading", config),
	destroy: (key?: string) => {
		if (key) {
			removeMessage(key);
		} else {
			messages = [];
			render();
		}
	},
};

export type { MessageConfig };
